
import { useState } from 'react';
import { supabase } from '../supabaseClient';

export const useTwilioOtp = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sendOtp = async (phone: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const { data, error } = await supabase.functions.invoke('TEST_twilio_send_otp', {
                body: { phone },
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            return data;
        } catch (err: any) {
            console.error('[Twilio] Send Error:', err);
            setError(err.message || 'Failed to send OTP');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const verifyOtp = async (params: {
        phone: string;
        otp: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        variant?: string;
        countryCode?: string;
        whatsappMarketingOptIn?: boolean;
    }) => {
        setIsLoading(true);
        setError(null);
        try {
            const { data, error } = await supabase.functions.invoke('TEST_msg91_verify_token', {
                body: {
                    ...params,
                    country_code: params.countryCode,
                    // Explicitly NO access_token for Twilio flow
                    access_token: null
                },
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            // Backend returns session data (token_hash)
            // Client must complete login with Supabase
            if (data?.token_hash) {
                const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
                    token_hash: data.token_hash,
                    type: 'magiclink' // Or 'sms' depending on what backend used, but backend used admin.generateLink type magiclink
                });

                if (sessionError) throw sessionError;
                return sessionData;
            } else if (data?.action_link) {
                // FIX: Extract token_hash from action_link instead of redirecting
                // This prevents localhost users from being redirected to the live website
                try {
                    const url = new URL(data.action_link);
                    const tokenHash = url.searchParams.get('token_hash') ||
                        (url.hash && new URLSearchParams(url.hash.substring(1)).get('token_hash'));

                    if (tokenHash) {
                        const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
                            token_hash: tokenHash,
                            type: data.type || 'magiclink'
                        });

                        if (sessionError) throw sessionError;
                        return sessionData;
                    }
                } catch (urlError) {
                    console.error('[Twilio] Failed to parse action_link:', urlError);
                }

                // Fallback: only redirect if we couldn't extract token_hash
                console.warn('[Twilio] Could not extract token_hash, falling back to redirect');
                window.location.href = data.action_link;
            }

            return data;
        } catch (err: any) {
            console.error('[Twilio] Verify Error:', err);
            setError(err.message || 'Failed to verify OTP');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        sendOtp,
        verifyOtp,
        isLoading,
        error,
    };
};
